import moment from 'moment';

export const COST_ANALYSIS_TYPES = [
  { value: 'line_item_unblended_cost', label: 'Unblended' },
  { value: 'line_item_amortized_cost', label: 'Amortized' },
  { value: 'line_item_blended_cost', label: 'Blended' },
  { value: 'pricing_public_on_demand_cost', label: 'List', isNew: true },
  {
    value: 'nops_line_item_net_unblended_cost',
    label: 'Discounted Unblended',
    isNew: true,
  },
  {
    value: 'nops_line_item_net_amortized_cost',
    label: 'Discounted Amortized',
    isNew: true,
  },
  {
    value: 'nops_pricing_net_public_on_demand_cost',
    label: 'Discounted List',
    isNew: true,
  },
];

// Dynamically generate COST_TYPE_NAMES to avoid duplication
export const COST_TYPE_NAMES = Object.fromEntries(COST_ANALYSIS_TYPES.map(({ value, label }) => [value, label]));

export const COST_TYPE_KEY_PARAMS = {
  // Old report mappings (keep these static to prevent breaking changes)
  UNBLENDED: 'line_item_unblended_cost',
  AMORTIZE: 'line_item_amortized_cost',
  BLENDED: 'line_item_blended_cost',

  // Dynamically generate new mappings from COST_ANALYSIS_TYPES
  ...Object.fromEntries(COST_ANALYSIS_TYPES.map(({ value }) => [value, value])),
};

export const COST_ANALYSIS_DIMENSION_KEYS = {
  COST_UNIT: 'pricing_unit',
  COST_ENTITY: 'bill_billing_entity',
  COST_ENTITY_LEGAL_NAME: 'line_item_legal_entity',
  ACCOUNTS: 'line_item_usage_account_id',
  ACCOUNTS_NAME: 'billing_project_name',
  ITEM_TYPE: 'line_item_line_item_type',
  PURCHASE_TYPE: 'nops_purchase_option',
  PRODUCT_SERVICES: 'product_product_name',
  INVOICE_SERVICE: 'product_servicename',
  PRODUCT_SERVICES_SUBTYPE: 'product_product_family',
  ENVIRONMENT: 'nops_final_environment',
  PRODUCT_REGIONS: 'product_region',
  REGION_NAME: 'nops_region_name',
  AVAILABILITY_ZONE: 'line_item_availability_zone',
  OPERATIONS: 'line_item_operation',
  PLATFORM: 'nops_operating_system_engine',
  USAGE_TYPES: 'line_item_usage_type',
  TAGS: 'tag_keys',
  COST_ALLOCATION_TAGS: 'cost_allocation_tag_keys',
  RESOURCES_ID: 'line_item_resource_id',
  RESOURCES_NAME: 'resource_name',
  NON_RESOURCES_V2: 'nops_non_taggable_resource',
  LINE_ITEM_DESCRIPTION: 'line_item_line_item_description',
  COMPUTE_TYPES: 'nops_compute_types',
  COMPUTE_PROVISIONER: 'nops_compute_provisioner',
  PRODUCT_INSTANCE_FAMILY: 'product_instance_family',
  PRODUCT_INSTANCE_TYPE: 'product_instance_type',
  PRODUCT_INSTANCE_TYPE_FAMILY: 'product_instance_type_family',
  STORAGE_PRODUCT: 'storage_product',
  STORAGE_PRODUCT_SUBTYPE: 'storage_product_subtype',
  STORAGE_CLASS: 'storage_class',
  STORAGE_TIER: 'storage_tier',
  STORAGE_OVERHEAD: 'storage_overhead',
  CLUSTER_ARN: 'cluster_arn',
  CLUSTER_NAME: 'cluster_name',
  NODE: 'node',
  PROVIDER_ID: 'providerID',
  POD_NAMESPACE: 'pod_namespace',
  CONTROLLER_KIND: 'controllerKind',
  CONTROLLER: 'controller',
  DEPLOYMENT: 'deployment',
  SERVICES: 'services',
  POD: 'pod',
  CONTAINER: 'container',
  LABELS: 'label_keys',
};

export const COST_ANALYSIS_DIMENSION_NAMES = {
  [COST_ANALYSIS_DIMENSION_KEYS.ACCOUNTS]: 'Accounts',
  [COST_ANALYSIS_DIMENSION_KEYS.ACCOUNTS_NAME]: 'Account Names',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_REGIONS]: 'Regions',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_SERVICES]: 'Product Services',
  [COST_ANALYSIS_DIMENSION_KEYS.RESOURCES_ID]: 'Resource ID',
  [COST_ANALYSIS_DIMENSION_KEYS.RESOURCES_NAME]: 'Resource Names',
  [COST_ANALYSIS_DIMENSION_KEYS.LINE_ITEM_DESCRIPTION]: 'Line Item Description',
  [COST_ANALYSIS_DIMENSION_KEYS.NON_RESOURCES_V2]: 'Non Resources',
  [COST_ANALYSIS_DIMENSION_KEYS.USAGE_TYPES]: 'Usage Type',
  [COST_ANALYSIS_DIMENSION_KEYS.OPERATIONS]: 'Operation',
  [COST_ANALYSIS_DIMENSION_KEYS.TAGS]: 'Tags',
  [COST_ANALYSIS_DIMENSION_KEYS.COST_ALLOCATION_TAGS]: 'Cost Allocation Tags',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_INSTANCE_FAMILY]: 'Instance Family',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_INSTANCE_TYPE]: 'Instance Type',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_INSTANCE_TYPE_FAMILY]: 'Instance Type Family',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_PRODUCT]: 'Storage Product',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_PRODUCT_SUBTYPE]: 'Storage Product Subtype',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_CLASS]: 'Storage Class',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_TIER]: 'Storage Tier',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_OVERHEAD]: 'Storage Overhead',
  [COST_ANALYSIS_DIMENSION_KEYS.ITEM_TYPE]: 'Line Item Type',
  [COST_ANALYSIS_DIMENSION_KEYS.COMPUTE_PROVISIONER]: 'Compute Provisioner',
  [COST_ANALYSIS_DIMENSION_KEYS.COMPUTE_TYPES]: 'Compute Types',
  [COST_ANALYSIS_DIMENSION_KEYS.PLATFORM]: 'Platform',
  [COST_ANALYSIS_DIMENSION_KEYS.PURCHASE_TYPE]: 'Purchase Type',
  [COST_ANALYSIS_DIMENSION_KEYS.ENVIRONMENT]: 'Environment',
  [COST_ANALYSIS_DIMENSION_KEYS.COST_ENTITY]: 'Cost Entity',
  [COST_ANALYSIS_DIMENSION_KEYS.COST_ENTITY_LEGAL_NAME]: 'Cost Entity Name',
  [COST_ANALYSIS_DIMENSION_KEYS.AVAILABILITY_ZONE]: 'Availability Zone',
  [COST_ANALYSIS_DIMENSION_KEYS.INVOICE_SERVICE]: 'Invoice Service',
  [COST_ANALYSIS_DIMENSION_KEYS.COST_UNIT]: 'Cost Unit',
  [COST_ANALYSIS_DIMENSION_KEYS.REGION_NAME]: 'Region Name',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_SERVICES_SUBTYPE]: 'Product Services Subtype',
  [COST_ANALYSIS_DIMENSION_KEYS.POD_NAMESPACE]: 'Namespace',
  [COST_ANALYSIS_DIMENSION_KEYS.NODE]: 'Node',
  [COST_ANALYSIS_DIMENSION_KEYS.PROVIDER_ID]: 'Instances',
  [COST_ANALYSIS_DIMENSION_KEYS.CONTROLLER_KIND]: 'Controller Kind',
  [COST_ANALYSIS_DIMENSION_KEYS.CONTROLLER]: 'Controller',
  [COST_ANALYSIS_DIMENSION_KEYS.DEPLOYMENT]: 'Deployment',
  [COST_ANALYSIS_DIMENSION_KEYS.SERVICES]: 'Services',
  [COST_ANALYSIS_DIMENSION_KEYS.POD]: 'Pod',
  [COST_ANALYSIS_DIMENSION_KEYS.CONTAINER]: 'Container',
  [COST_ANALYSIS_DIMENSION_KEYS.CLUSTER_ARN]: 'Cluster ARN',
  [COST_ANALYSIS_DIMENSION_KEYS.CLUSTER_NAME]: 'Cluster Name',
  [COST_ANALYSIS_DIMENSION_KEYS.LABELS]: 'Labels',
};

export const COST_ANALYSIS_FILTER_DIMENSION = {
  ...Object.fromEntries(Object.entries(COST_ANALYSIS_DIMENSION_KEYS).map(([key, value]) => [value, value])),
  [COST_ANALYSIS_DIMENSION_KEYS.ACCOUNTS]: 'account_id',
  [COST_ANALYSIS_DIMENSION_KEYS.ITEM_TYPE]: 'item_type',
  [COST_ANALYSIS_DIMENSION_KEYS.OPERATIONS]: 'operation',
  [COST_ANALYSIS_DIMENSION_KEYS.USAGE_TYPES]: 'usagetype',
  [COST_ANALYSIS_DIMENSION_KEYS.RESOURCES_ID]: 'resource_id',
  [COST_ANALYSIS_DIMENSION_KEYS.LINE_ITEM_DESCRIPTION]: 'description',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_REGIONS]: 'region',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_SERVICES]: 'product',
};

export const BC_PLUS_TABS = [
  COST_ANALYSIS_DIMENSION_KEYS.COST_UNIT,
  COST_ANALYSIS_DIMENSION_KEYS.COST_ENTITY,
  COST_ANALYSIS_DIMENSION_KEYS.COST_ENTITY_LEGAL_NAME,
  COST_ANALYSIS_DIMENSION_KEYS.INVOICE_SERVICE,
  COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_SERVICES_SUBTYPE,
  COST_ANALYSIS_DIMENSION_KEYS.REGION_NAME,
  COST_ANALYSIS_DIMENSION_KEYS.AVAILABILITY_ZONE,
  COST_ANALYSIS_DIMENSION_KEYS.COST_ALLOCATION_TAGS,
  COST_ANALYSIS_DIMENSION_KEYS.NON_RESOURCES_V2,
  COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_INSTANCE_FAMILY,
  COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_INSTANCE_TYPE,
  COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_INSTANCE_TYPE_FAMILY,
  COST_ANALYSIS_DIMENSION_KEYS.STORAGE_PRODUCT,
  COST_ANALYSIS_DIMENSION_KEYS.STORAGE_PRODUCT_SUBTYPE,
  COST_ANALYSIS_DIMENSION_KEYS.STORAGE_CLASS,
  COST_ANALYSIS_DIMENSION_KEYS.STORAGE_TIER,
  COST_ANALYSIS_DIMENSION_KEYS.STORAGE_OVERHEAD,
  COST_ANALYSIS_DIMENSION_KEYS.CLUSTER_ARN,
  COST_ANALYSIS_DIMENSION_KEYS.CLUSTER_NAME,
  COST_ANALYSIS_DIMENSION_KEYS.NODE,
  COST_ANALYSIS_DIMENSION_KEYS.PROVIDER_ID,
  COST_ANALYSIS_DIMENSION_KEYS.POD_NAMESPACE,
  COST_ANALYSIS_DIMENSION_KEYS.CONTROLLER_KIND,
  COST_ANALYSIS_DIMENSION_KEYS.CONTROLLER,
  COST_ANALYSIS_DIMENSION_KEYS.DEPLOYMENT,
  COST_ANALYSIS_DIMENSION_KEYS.SERVICES,
  COST_ANALYSIS_DIMENSION_KEYS.POD,
  COST_ANALYSIS_DIMENSION_KEYS.CONTAINER,
  COST_ANALYSIS_DIMENSION_KEYS.LABELS,
];
export const TABS = Object.entries(COST_ANALYSIS_DIMENSION_KEYS).map(([key, value]) => ({
  id: value,
  name: COST_ANALYSIS_DIMENSION_NAMES[value] || key,
  isNew: BC_PLUS_TABS.includes(value),
}));

export const GRANULARITY = {
  hour: 'Hourly',
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  quarter: 'Quarterly',
};

export const GRANULARITY_TYPE = ({ date_start = null, date_end = null }, profile, range) => {
  if (!date_start || !date_end) {
    return GRANULARITY;
  }

  const nOpsUser = (profile?.email || '').includes('@nops.io');

  const build = keys =>
    keys.reduce((acc, key) => {
      if (GRANULARITY[key]) {
        acc[key] = GRANULARITY[key];
      }

      return acc;
    }, {});

  if (['Remaining This Month', '+1M'].includes(range) && !nOpsUser) {
    return build(['hour', 'day', 'week']);
  }

  if (['+3M', '+6M', '+1Y'].includes(range) && !nOpsUser) {
    return build(['month', 'quarter']);
  }

  const startMoment = moment(date_start, 'YYYY-MM-DD');
  const endMoment = moment(date_end, 'YYYY-MM-DD');
  const daysDiff = endMoment.diff(startMoment, 'days');
  const monthDiff = endMoment.diff(startMoment, 'months', true);
  let keys;

  if (daysDiff < 7) {
    keys = ['hour', 'day'];
  } else if (daysDiff < 14) {
    keys = ['hour', 'day', 'week'];
  } else if (daysDiff < 90) {
    keys = ['hour', 'day', 'week', 'month'];
  } else {
    keys = Object.keys(GRANULARITY);
  }

  if (!nOpsUser) {
    if (monthDiff > 3) {
      keys = keys.filter(k => k !== 'hour');
    }

    if (monthDiff > 6) {
      keys = keys.filter(k => k !== 'day');
    }
  }

  return build(keys);
};

export const REPORT_DIMENSION = {
  ACCOUNT: COST_ANALYSIS_DIMENSION_KEYS.ACCOUNTS,
  REGION: COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_REGIONS,
  SERVICE: COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_SERVICES,
  RESOURCE: COST_ANALYSIS_DIMENSION_KEYS.RESOURCES_ID,
  DESCRIPTION: COST_ANALYSIS_DIMENSION_KEYS.LINE_ITEM_DESCRIPTION,
  USAGETYPE: COST_ANALYSIS_DIMENSION_KEYS.USAGE_TYPES,
  OPERATION: COST_ANALYSIS_DIMENSION_KEYS.OPERATIONS,
  TAG: COST_ANALYSIS_DIMENSION_KEYS.TAGS,
  ITEM_TYPE: COST_ANALYSIS_DIMENSION_KEYS.ITEM_TYPE,
};

export const ACTION_ITEMS = [
  { label: 'Create New Report', value: 'create-report' },
  { label: 'Update Existing Report', value: 'update-report' },
  { label: 'View All Reports', value: 'manage-report' },
  { label: 'Send Report', value: 'send-report' },
];

export const DEFINITIONS = {
  line_item_unblended_cost:
    "The actual after-discount cost you're paying for the line item. This cost is included in your report only if a discount applies to your account in the billing period.",
  line_item_amortized_cost:
    'Similar to unblended cost, but accounts for Reserved Instances (RI) and Savings Plans (SP).',
  line_item_blended_cost:
    'The cost is calculated by multiplying the BlendedRate with the UsageAmount. Blended costs apply when usage is aggregated across multiple linked accounts in AWS Organizations.',
  pricing_public_on_demand_cost: 'The public on-demand rate for a service without any discounts applied.',
  nops_line_item_net_unblended_cost: `The unblended cost after applying applicable discounts such as PPA's, EDP's, etc.`,
  nops_line_item_net_amortized_cost: `Similar to Amortized Cost, but accounting for any discounts you may have received from PPA's, EDP's, etc.`,
  nops_pricing_net_public_on_demand_cost: 'The list cost with all applicable discounts accounted for.',
  [COST_ANALYSIS_DIMENSION_KEYS.COST_UNIT]: 'The unit of measurement for a resource (e.g., hours, GB, or requests).',
  [COST_ANALYSIS_DIMENSION_KEYS.COST_ENTITY]:
    'The billing entity responsible for charges (e.g., AWS, AWS Marketplace vendors).',
  [COST_ANALYSIS_DIMENSION_KEYS.COST_ENTITY_LEGAL_NAME]:
    'The Seller of Record for a specific service (e.g., Amazon Web Services, Inc. or Databricks).',
  [COST_ANALYSIS_DIMENSION_KEYS.ACCOUNTS]:
    'The account ID that incurred the usage. For AWS Organizations, this can be the management or a member account.',
  [COST_ANALYSIS_DIMENSION_KEYS.ACCOUNTS_NAME]:
    'The human-readable name of the billing project. This is the name of the account given on the nOps platform, it can also be inherited from your AWS organizations.',
  [COST_ANALYSIS_DIMENSION_KEYS.ITEM_TYPE]:
    'The type of line item in the report (e.g., Usage, Tax, Discount, RIFee, SavingsPlanRecurringFee).',
  [COST_ANALYSIS_DIMENSION_KEYS.PURCHASE_TYPE]: 'The instance purchasing option (e.g., On-Demand, Reserved, Spot).',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_SERVICES]:
    'The specific AWS service or Marketplace product (e.g., Amazon Elastic Compute Cloud, Amazon Relational Database Service).',
  [COST_ANALYSIS_DIMENSION_KEYS.INVOICE_SERVICE]:
    'The service name listed on the invoice (e.g., Amazon S3 or AWS Lambda).',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_SERVICES_SUBTYPE]:
    'A broader classification of services (e.g., ComputeSavingsPlans, Storage).',
  [COST_ANALYSIS_DIMENSION_KEYS.ENVIRONMENT]:
    'Derived from tags or project name. In case of a tie, tags take precedence.',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_REGIONS]: 'The code of the AWS region hosting the service.',
  [COST_ANALYSIS_DIMENSION_KEYS.REGION_NAME]: 'A human-readable name for the AWS region (e.g., US West (Oregon)).',
  [COST_ANALYSIS_DIMENSION_KEYS.AVAILABILITY_ZONE]:
    'The AWS Availability Zone that hosted the resource (e.g., us-east-1a).',
  [COST_ANALYSIS_DIMENSION_KEYS.OPERATIONS]:
    'The specific operation associated with the resource, such as RunInstances or GetObject.',
  [COST_ANALYSIS_DIMENSION_KEYS.PLATFORM]:
    'The operating system or engine of the resource (e.g., Linux, Windows, SQL Server).',
  [COST_ANALYSIS_DIMENSION_KEYS.USAGE_TYPES]:
    'The usage details, such as the specific type of instance or service used (e.g., BoxUsage:m5.large).',
  [COST_ANALYSIS_DIMENSION_KEYS.TAGS]: 'Metadata assigned to resources for identification and grouping.',
  [COST_ANALYSIS_DIMENSION_KEYS.COST_ALLOCATION_TAGS]:
    'User-defined tags that are used to allocate costs across departments, projects, or teams.',
  [COST_ANALYSIS_DIMENSION_KEYS.RESOURCES_ID]:
    'The unique identifier of the resource, such as an EC2 instance ID or S3 bucket name.',
  [COST_ANALYSIS_DIMENSION_KEYS.COMPUTE_TYPES]:
    'The types of compute resources in use (e.g., EC2, Lambda, or Fargate).',
  [COST_ANALYSIS_DIMENSION_KEYS.COMPUTE_PROVISIONER]:
    'Indicates the entity responsible for provisioning compute resources, such as auto-scaling groups or Kubernetes controllers.',
  [COST_ANALYSIS_DIMENSION_KEYS.RESOURCES_NAME]: 'The name of a resource if it has a Name tag.',
  [COST_ANALYSIS_DIMENSION_KEYS.NON_RESOURCES_V2]: 'Resources that cannot be tagged, such as data transfer or taxes.',
  [COST_ANALYSIS_DIMENSION_KEYS.LINE_ITEM_DESCRIPTION]:
    'The description of the line item, including the type of resource, usage details, or any discounts applied.',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_INSTANCE_TYPE]: 'The specific type of an instance (e.g., m5.large, t3.micro).',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_INSTANCE_FAMILY]:
    'The family of the instance type (e.g., Computer optimized, General purpose).',
  [COST_ANALYSIS_DIMENSION_KEYS.PRODUCT_INSTANCE_TYPE_FAMILY]:
    'Groups of related instance types (e.g., all m5 instances).',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_PRODUCT]: 'The specific storage product name. (e.g., S3, EBS, FSx).',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_PRODUCT_SUBTYPE]:
    'A classification of storage usage categories. (e.g., Storage, Access, Data Transfer).',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_CLASS]:
    'Different levels of storage products, optimizing for cost, performance, and durability. (e.g., Standard, One Zone, SSD, Glacier).',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_TIER]:
    'Performance and access tiers defining throughput, volume types, and retrieval characteristics. (e.g., Frequent Access, 500 MB/s/TiB, Instant Retrieval)',
  [COST_ANALYSIS_DIMENSION_KEYS.STORAGE_OVERHEAD]:
    'Metadata, system processes, and additional storage consumption beyond user data. (e.g., Small File Overhead, Object Tag, IT Monitoring Fee).',
  [COST_ANALYSIS_DIMENSION_KEYS.CLUSTER_ARN]:
    'The Amazon Resource Name (ARN) of the cluster associated with the resource.',
  [COST_ANALYSIS_DIMENSION_KEYS.CLUSTER_NAME]: 'The name of the cluster where the resource is running.',
  [COST_ANALYSIS_DIMENSION_KEYS.NODE]: 'The node or host machine where the resource is running.',
  [COST_ANALYSIS_DIMENSION_KEYS.PROVIDER_ID]: 'The unique identifier for instances provisioned by the cloud provider.',
  [COST_ANALYSIS_DIMENSION_KEYS.POD_NAMESPACE]: 'The Kubernetes namespace is associated with a resource.',
  [COST_ANALYSIS_DIMENSION_KEYS.CONTROLLER_KIND]:
    'Specifies the type of controller, such as Deployment, StatefulSet, Job, or DaemonSet.',
  [COST_ANALYSIS_DIMENSION_KEYS.CONTROLLER]:
    'The entity that managesmanaging deployments, such as a Kubernetes controller.',
  [COST_ANALYSIS_DIMENSION_KEYS.DEPLOYMENT]: 'The name of the deployment responsible for running the resource.',
  [COST_ANALYSIS_DIMENSION_KEYS.SERVICES]: 'General services or applications running on the resources.',
  [COST_ANALYSIS_DIMENSION_KEYS.POD]: 'A Kubernetes pod represents a group of containers running on a node.',
  [COST_ANALYSIS_DIMENSION_KEYS.CONTAINER]: 'The specific container within a Kubernetes pod.',
  [COST_ANALYSIS_DIMENSION_KEYS.LABELS]:
    'Key-value pairs are assigned to resources for organization and identification.',
};

// ---------------------------
// Constants
// ---------------------------
export const HEAVY_TABS = new Set([
  COST_ANALYSIS_DIMENSION_KEYS.RESOURCES_ID,
  COST_ANALYSIS_DIMENSION_KEYS.RESOURCES_NAME,
  COST_ANALYSIS_DIMENSION_KEYS.LINE_ITEM_DESCRIPTION,
  COST_ANALYSIS_DIMENSION_KEYS.NON_RESOURCES_V2,
  COST_ANALYSIS_DIMENSION_KEYS.USAGE_TYPES,
  COST_ANALYSIS_DIMENSION_KEYS.OPERATIONS,
]);
